import type { Node } from '../../../types'

// ---------------------------------------------------------------------------
// Schema field types – describes how a JSON value should be rendered/edited
// ---------------------------------------------------------------------------

interface BaseField {
  /** When `true` the field renders as an editable control. Default: false */
  editable?: boolean
}

export interface StringField extends BaseField {
  type: 'string'
}

export interface NumberField extends BaseField {
  type: 'number'
  min?: number
  max?: number
}

export interface BooleanField extends BaseField {
  type: 'boolean'
}

export interface EnumField extends BaseField {
  type: 'enum'
  options: string[]
}

export interface ObjectField extends BaseField {
  type: 'object'
  properties: Record<string, JsonFieldSchema>
  /**
   * Allow deleting keys from the object.
   *
   * Note: `allowAdd` is intentionally **not** supported on objects.
   * Adding a key creates a data entry but without a corresponding schema entry
   * the new field cannot be edited or typed. A strategy for dynamically adding
   * schema entries alongside data keys is needed before this can be implemented.
   *
   * @default false
   */
  allowDelete?: boolean
}

export interface ArrayField extends BaseField {
  type: 'array'
  items: JsonFieldSchema
  /** Allow adding new items to the array. Default: false */
  allowAdd?: boolean
  /** Allow deleting items from the array. Default: false */
  allowDelete?: boolean
}

export type JsonFieldSchema =
  | StringField
  | NumberField
  | BooleanField
  | EnumField
  | ObjectField
  | ArrayField

// ---------------------------------------------------------------------------
// Top-level editor schema
// ---------------------------------------------------------------------------

/**
 * Top-level schema for `JsonEditor`.
 *
 * Two complementary approaches for describing editability:
 *
 * **`root` (tree)** — mirrors the data shape. Best for uniform / shallow data
 * where every array element shares one schema:
 * ```
 * data:   [{ enabled: true, code: "FOO" }, { enabled: false, code: "BAR" }]
 * schema: { root: { type: 'array', editable: true, items: {
 *   type: 'object', editable: true, properties: {
 *     enabled: { type: 'boolean', editable: true },
 *     code:    { type: 'enum', editable: true, options: ['FOO','BAR','BAZ'] }
 * }}}}
 * ```
 *
 * **`overrides` (flat dot-paths)** — targets specific leaves without mirroring
 * intermediate structure. Best for deeply nested or heterogeneous data:
 * ```
 * data:   { query: { and: { expressions: [
 *   { equals: { fields: 'state', values: ['CA','TX'] } },
 *   { equals: { fields: 'status', values: ['OPEN'] } },
 * ]}}, size: 100 }
 * schema: {
 *   root: { type: 'object', properties: {} },
 *   overrides: {
 *     'query.and.expressions.0.equals.values': { type:'array', editable:true, items:{ type:'enum', options:['CA','TX','NY'] }},
 *     'query.and.expressions.1.equals.values': { type:'array', editable:true, items:{ type:'enum', options:['OPEN','CLOSED'] }},
 *   }
 * }
 * ```
 *
 * Both can be combined: use `root` for structural parts, `overrides` to
 * punch through where the tree would be verbose. Overrides are checked
 * first; unmatched fields fall back to `root` resolution, then read-only.
 */
export interface JsonEditorSchema {
  /** Tree-structured schema mirroring the data shape. */
  root: JsonFieldSchema
  /**
   * Dot-path overrides checked before `root` resolution.
   * Keys use numeric indices for array elements,
   * e.g. `'query.and.expressions.0.equals.values'`.
   */
  overrides?: Record<string, JsonFieldSchema>
}

// ---------------------------------------------------------------------------
// Type-level mapping: JsonFieldSchema → TypeScript type
//
// This allows consumers to pass a generic to the component so that the
// compiler can structurally verify that `data` matches the `schema`.
// ---------------------------------------------------------------------------

/**
 * Maps a `JsonFieldSchema` to the TypeScript type it describes.
 *
 * Usage:
 *   type MyData = SchemaToType<typeof mySchema.root>
 *   <JsonEditor<MyData> data={myData} schema={mySchema} onChange={...} />
 */
export type SchemaToType<S extends JsonFieldSchema> = S extends StringField
  ? string
  : S extends NumberField
  ? number
  : S extends BooleanField
  ? boolean
  : S extends EnumField
  ? string
  : S extends ObjectField
  ? { [K in keyof S['properties']]: SchemaToType<S['properties'][K]> }
  : S extends ArrayField
  ? SchemaToType<S['items']>[]
  : Node

// ---------------------------------------------------------------------------
// Runtime helpers
// ---------------------------------------------------------------------------

/**
 * Look up the sub-schema for a given key inside a parent schema.
 * Returns `undefined` when no schema is available (→ read-only fallback).
 */
export const resolveFieldSchema = (
  schema: JsonFieldSchema | undefined,
  key: string | number
): JsonFieldSchema | undefined => {
  if (!schema) return undefined
  if (schema.type === 'object') return schema.properties?.[key as string]
  if (schema.type === 'array') return schema.items
  return undefined
}

/**
 * Check whether a schema's declared type matches the runtime value.
 */
export const schemaMatchesValue = (
  schema: JsonFieldSchema,
  value: Node
): boolean => {
  if (value === null) return false
  switch (schema.type) {
    case 'string':
      return typeof value === 'string'
    case 'number':
      return typeof value === 'number'
    case 'boolean':
      return typeof value === 'boolean'
    case 'enum':
      return typeof value === 'string'
    case 'object':
      return typeof value === 'object' && !Array.isArray(value)
    case 'array':
      return Array.isArray(value)
    default:
      return false
  }
}

/**
 * Generate a default value from a field schema.
 * Used when adding a new item to an editable array.
 */
export const generateDefault = (schema: JsonFieldSchema): Node => {
  switch (schema.type) {
    case 'string':
      return ''
    case 'number':
      return 0
    case 'boolean':
      return false
    case 'enum':
      return schema.options[0] ?? ''
    case 'object':
      return Object.fromEntries(
        Object.entries(schema.properties).map(([k, v]) => [
          k,
          generateDefault(v),
        ])
      )
    case 'array':
      return []
    default:
      return null
  }
}

// ---------------------------------------------------------------------------
// Immutable deep-update helper
// ---------------------------------------------------------------------------

/**
 * Immutably update a `Node` tree at the given path, returning a new tree.
 */
export const updateAtPath = (
  data: Node,
  path: (string | number)[],
  value: Node
): Node => {
  if (path.length === 0) return value

  const [head, ...rest] = path

  if (Array.isArray(data)) {
    const index = typeof head === 'number' ? head : parseInt(String(head), 10)
    const newArr = [...data]
    newArr[index] = updateAtPath(newArr[index], rest, value)
    return newArr
  }

  if (typeof data === 'object' && data !== null) {
    const record = data as Record<string, Node>
    return {
      ...record,
      [head]: updateAtPath(record[head as string], rest, value),
    }
  }

  // Primitive at a level that expects nesting – just return the value
  return value
}

/**
 * Immutably remove an item at `index` from an array `Node`.
 */
export const removeAtIndex = (data: Node, index: number): Node => {
  if (!Array.isArray(data)) return data
  return [...data.slice(0, index), ...data.slice(index + 1)]
}

/**
 * Immutably append a value to an array `Node`.
 */
export const appendToArray = (data: Node, value: Node): Node => {
  if (!Array.isArray(data)) return data
  return [...data, value]
}

/**
 * Immutably remove a key from an object `Node`.
 */
export const removeKey = (data: Node, key: string): Node => {
  if (typeof data !== 'object' || data === null || Array.isArray(data))
    return data
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { [key]: _removed, ...rest } = data as Record<string, Node>
  return rest
}
